/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Roles & Permissions React Query Hooks
 * Queries and mutations for role CRUD with cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { rolesService } from '../services/roles.service';
import { useAuthStore } from '@/stores/authStore';
import type { RoleDto, PermissionDto, CreateRoleRequest, UpdatePermissionsRequest } from '../services/roles.service';
import type { ApiError } from '@/services/api/types';

// Query key factory for cache management
export const roleKeys = {
  all: ['venta', 'admin', 'roles'] as const,
  lists: () => [...roleKeys.all, 'list'] as const,
  details: () => [...roleKeys.all, 'detail'] as const,
  detail: (id: string) => [...roleKeys.details(), id] as const,
  permissions: () => [...roleKeys.all, 'permissions'] as const,
};

/**
 * Fetch all roles
 */
export function useRoles() {
  return useQuery<RoleDto[], ApiError>({
    queryKey: roleKeys.lists(),
    queryFn: () => rolesService.listRoles(),
    staleTime: 30_000,
  });
}

/**
 * Fetch all available permissions
 */
export function usePermissions() {
  return useQuery<PermissionDto[], ApiError>({
    queryKey: roleKeys.permissions(),
    queryFn: () => rolesService.listPermissions(),
    staleTime: 60_000,
  });
}

/**
 * Fetch a single role by ID
 */
export function useRole(id: string | undefined) {
  return useQuery<RoleDto, ApiError>({
    queryKey: roleKeys.detail(id!),
    queryFn: () => rolesService.getRole(id!),
    enabled: !!id,
  });
}

/**
 * Create a new role
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation<RoleDto, ApiError, CreateRoleRequest>({
    mutationFn: rolesService.createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
}

/**
 * Update permissions for a role
 */
export function useUpdatePermissions() {
  const queryClient = useQueryClient();

  return useMutation<RoleDto, ApiError, { id: string; data: UpdatePermissionsRequest }>({
    mutationFn: ({ id, data }) => rolesService.updatePermissions(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(id) });
    },
  });
}

/**
 * Delete a role
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: rolesService.deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
}

/**
 * Reset a role to its default permissions
 */
export function useResetRoleDefaults() {
  const queryClient = useQueryClient();

  return useMutation<RoleDto, ApiError, string>({
    mutationFn: rolesService.resetToDefault,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(id) });
    },
  });
}

/**
 * Fetch current user's permissions and store them in authStore.
 * Call this from any VENTA layout/sidebar component — it runs once on mount
 * and syncs to Zustand for immediate use throughout the app.
 */
export function useMyPermissions() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setPermissions = useAuthStore((s) => s.setPermissions);

  const query = useQuery<string[], ApiError>({
    queryKey: ['venta', 'me', 'permissions'],
    queryFn: () => rolesService.getMyPermissions(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000, // 5 minutes
  });

  // Sync to authStore whenever data arrives
  useEffect(() => {
    if (query.data) {
      setPermissions(query.data);
    }
  }, [query.data, setPermissions]);

  return query;
}
